import { Skeleton } from "@/components/ui/skeleton";

/* ─── Collectives Directory Grid ─────────────────────────────────────────── */

export function CollectivesGridSkeleton({ count = 6 }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="collectives-skeleton">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white border border-bone-dark rounded-lg overflow-hidden">
          <Skeleton className="h-36 w-full bg-navy/5" />
          <div className="p-5 space-y-3">
            <Skeleton className="h-6 w-3/4 bg-navy/8" />
            <Skeleton className="h-4 w-1/2 bg-gold/10" />
            <Skeleton className="h-4 w-2/3 bg-gold/10" />
            <Skeleton className="h-4 w-1/3 bg-navy/5" />
            <Skeleton className="h-12 w-full bg-bone" />
            <div className="flex justify-between pt-3 border-t border-bone-dark">
              <Skeleton className="h-3 w-24 bg-navy/5" />
              <Skeleton className="h-3 w-12 bg-gold/10" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Videos Grid ────────────────────────────────────────────────────────── */

export function VideosGridSkeleton({ count = 6 }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="videos-skeleton">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow-lg overflow-hidden">
          <Skeleton className="w-full pb-[56.25%] bg-navy/8" />
          <div className="p-6 space-y-3">
            <Skeleton className="h-5 w-16 rounded bg-navy/10" />
            <Skeleton className="h-5 w-full bg-navy/8" />
            <Skeleton className="h-4 w-5/6 bg-navy/5" />
            <Skeleton className="h-3 w-1/4 bg-navy/5 mt-2" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Collective Room — Tab Content ──────────────────────────────────────── */

export function RoomHeaderSkeleton() {
  return (
    <div className="bg-navy text-white px-4 sm:px-6 lg:px-8 py-6" data-testid="room-header-skeleton">
      <div className="max-w-4xl mx-auto space-y-3">
        <Skeleton className="h-4 w-28 bg-white/10" />
        <Skeleton className="h-8 w-64 bg-white/15" />
        <Skeleton className="h-4 w-48 bg-white/10" />
        <div className="flex gap-4 mt-2">
          <Skeleton className="h-4 w-32 bg-white/8" />
          <Skeleton className="h-4 w-40 bg-white/8" />
        </div>
      </div>
    </div>
  );
}

export function AnnouncementsSkeleton({ count = 4 }) {
  return (
    <div className="space-y-3" data-testid="announcements-skeleton">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white border border-bone-dark rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-8 rounded-full bg-navy/8" />
            <Skeleton className="h-4 w-24 bg-navy/8" />
            <Skeleton className="h-3 w-16 bg-navy/5 ml-auto" />
          </div>
          <Skeleton className="h-4 w-full bg-navy/5" />
          <Skeleton className="h-4 w-3/4 bg-navy/5" />
        </div>
      ))}
    </div>
  );
}

export function PrayerWallSkeleton({ count = 4 }) {
  return (
    <div className="space-y-3" data-testid="prayer-skeleton">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-bone/50 border border-bone-dark rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-6 rounded-full bg-gold/15" />
            <Skeleton className="h-4 w-20 bg-navy/8" />
          </div>
          <Skeleton className="h-4 w-full bg-navy/5" />
          <Skeleton className="h-4 w-2/3 bg-navy/5" />
          <Skeleton className="h-3 w-24 bg-gold/10" />
        </div>
      ))}
    </div>
  );
}

export function FilesSkeleton({ count = 3 }) {
  return (
    <div className="space-y-2" data-testid="files-skeleton">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 bg-white border border-bone-dark rounded-lg p-3">
          <Skeleton className="h-10 w-10 rounded bg-navy/8 shrink-0" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-3/4 bg-navy/8" />
            <Skeleton className="h-3 w-1/3 bg-navy/5" />
          </div>
          <Skeleton className="h-8 w-8 rounded bg-navy/5 shrink-0" />
        </div>
      ))}
    </div>
  );
}

/* ─── Home Page — Events Grid (Desktop) ──────────────────────────────────── */

export function EventsGridSkeleton({ count = 3 }) {
  return (
    <div className="hidden md:grid md:grid-cols-3 gap-8" data-testid="events-grid-skeleton">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white border-l-4 border-gold/30 rounded-lg overflow-hidden p-6 space-y-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-20 rounded-sm bg-navy/8" />
            <Skeleton className="h-5 w-16 rounded-sm bg-gold/10" />
          </div>
          <Skeleton className="h-6 w-3/4 bg-navy/8" />
          <Skeleton className="h-4 w-1/3 bg-navy/5" />
          <Skeleton className="h-4 w-full bg-bone-dark" />
          <Skeleton className="h-4 w-2/3 bg-bone-dark" />
        </div>
      ))}
    </div>
  );
}

/* ─── Home Page — Events List (Mobile) ───────────────────────────────────── */

export function EventsListSkeleton({ count = 4 }) {
  return (
    <div className="md:hidden space-y-3" data-testid="events-list-skeleton">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 bg-white p-4 border-l-4 border-gold/30 rounded-sm">
          <Skeleton className="h-12 w-[52px] rounded bg-navy/8 shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-3/4 bg-navy/8" />
            <Skeleton className="h-3 w-1/3 bg-navy/5" />
          </div>
          <Skeleton className="h-4 w-4 rounded bg-bone-dark shrink-0" />
        </div>
      ))}
    </div>
  );
}

/* ─── Home Page — Verse of the Day ───────────────────────────────────────── */

export function VerseSkeleton() {
  return (
    <div className="space-y-4 py-2" data-testid="verse-skeleton">
      <Skeleton className="h-6 w-4/5 mx-auto bg-white/8" />
      <Skeleton className="h-6 w-3/5 mx-auto bg-white/8" />
      <Skeleton className="h-6 w-2/3 mx-auto bg-white/8" />
      <Skeleton className="h-4 w-32 mx-auto bg-gold/15 mt-2" />
    </div>
  );
}

/* ─── Media Page — Video Grid (themed replacement) ───────────────────────── */

export function MediaVideosSkeleton({ count = 6 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="media-videos-skeleton">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white rounded-xl overflow-hidden border border-bone-dark">
          <Skeleton className="w-full pb-[56.25%] bg-navy/6" />
          <div className="p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-16 rounded-full bg-navy/8" />
            </div>
            <Skeleton className="h-4 w-full bg-navy/6" />
            <Skeleton className="h-4 w-3/4 bg-navy/5" />
            <Skeleton className="h-3 w-24 bg-bone-dark mt-1" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Giving — PayPal SDK Loading ────────────────────────────────────────── */

export function PayPalButtonSkeleton() {
  return (
    <div className="space-y-3" data-testid="paypal-button-skeleton">
      <Skeleton className="h-[45px] w-full rounded bg-[#ffc439]/20" />
      <Skeleton className="h-[45px] w-full rounded bg-navy/8" />
    </div>
  );
}

/* ─── Host Dashboard ─────────────────────────────────────────────────────── */

export function HostDashboardSkeleton() {
  return (
    <div className="container-narrow py-8 space-y-8" data-testid="host-dashboard-skeleton">
      {/* Pending section */}
      <div className="space-y-3">
        <Skeleton className="h-6 w-48 bg-navy/8" />
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 bg-white border border-bone-dark rounded-lg p-4">
            <Skeleton className="h-10 w-10 rounded-full bg-gold/10 shrink-0" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-40 bg-navy/8" />
              <Skeleton className="h-3 w-28 bg-navy/5" />
            </div>
            <Skeleton className="h-8 w-20 rounded bg-green-100 shrink-0" />
            <Skeleton className="h-8 w-20 rounded bg-red-100 shrink-0" />
          </div>
        ))}
      </div>
      {/* Reviewed section */}
      <div className="space-y-3">
        <Skeleton className="h-6 w-32 bg-navy/8" />
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 bg-white border border-bone-dark rounded-lg p-4">
            <Skeleton className="h-10 w-10 rounded-full bg-navy/5 shrink-0" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-36 bg-navy/8" />
              <Skeleton className="h-3 w-24 bg-navy/5" />
            </div>
            <Skeleton className="h-6 w-16 rounded bg-navy/5 shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}
